import React from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { Box, Button } from '@mui/material';
import { isAxiosError } from 'axios';
import ReuseableTable from '../../table/ReuseableTable';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';
import OutlinedPageBox from '../../global/OutlinedPageBox';
import CenteredContentLayout from '../../global/CenteredContentLayout';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  CreateWaiverPayload,
  createWaiverSchema,
} from '../../../types/waivers';
import useUser from '../../../hooks/global/useUser';
import useNewWaiver from '../../../hooks/waivers/useNewWaiver';
import useSelectWaiver from '../../../hooks/waivers/useSelectWaiver';
import { LoadedUser, Row } from '../../../interfaces';
import TableDropdownMenu from '../../global/TableDropdownMenu';
import PageLoading from '../../global/PageLoading';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';
import useWaiverTableActions from '../../../hooks/waivers/useWaiverTableActions';
import useSubscriptionUpsell from '../../../hooks/subscriptions/useSubscriptionUpsell';
import { Feature } from '../../../types/subscriptions';
import useConfirm from '../../../hooks/useConfirm';
import useSubscriptionModal from '../../../hooks/subscriptions/useSubscriptionModal';

function WaiversTable() {
  const { data, isLoading } = useGetWaivers();
  const { user } = useUser<LoadedUser>();
  const { onSelectWaiver, defaultWaiver } = useSelectWaiver();
  const {
    editWaiver,
    viewWaiverSubmissions,
    renameWaiver,
    previewWaiver,
    archiveWaiver,
  } = useWaiverTableActions();
  const { requireUpsell, upsell, isSubscriptionActive } = useSubscriptionUpsell(
    Feature.WAIVERS,
  );
  const { openSubscriptionModal } = useSubscriptionModal();
  const confirm = useConfirm();

  const [newWaiverForm, setNewWaiverForm] = React.useState<FormData | null>(
    null,
  );

  const { mutation: updateMutation } = useUpdateWaiver();
  const { mutation: newWaiverMutation, isPending: isPendingNew } = useNewWaiver(
    {
      onSuccess: newWaiver => {
        if (!newWaiver?.id) return;
        toast.success('Waiver created successfully');
        onSelectWaiver(newWaiver.id, { isNew: true });
      },
      onError: error => {
        if (isAxiosError(error) && error.response?.status === 409) {
          toast.error('Waiver with this name already exists');
        } else {
          toast.error('Failed to create waiver');
        }
      },
    },
  );

  const hasNoUpdatedWaivers =
    !data?.length ||
    (data.length === 1 &&
      !data.some(w => new Date(w.updatedAt) > new Date(w.createdAt)));

  const waiverForm: FormData<CreateWaiverPayload> = {
    initialValues: {
      businessId: user?.business?.id,
      name: '',
      serviceIds: [],
    },
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Waiver Name',
          placeholder: 'Enter waiver name',
          name: 'name',
          disabled: isPendingNew,
          tooltip: 'The name of the waiver. Must be unique.',
        },
      ],
    ],
    onSubmit: async values => {
      if (values.name.toLowerCase().includes('minor')) {
        await confirm({
          title: 'Minors are automatically handled in Porter',
          message:
            "When a minor is filling out a waiver, we'll automatically apply the settings from your Manage Shop > Booking > Minor Settings page to the waiver. You can also update settings for individual services, like piercings, from the Manage Shop > Booking > Other Services section.",
          positiveText: 'OK',
          negativeText: undefined,
        });
        return;
      }

      const newWaiver = await newWaiverMutation.mutateAsync(values);
      if (newWaiver?.id) {
        onSelectWaiver(newWaiver.id);
        setNewWaiverForm(null);
      }
    },
    title: 'Create a new waiver',
    submitButtonText: 'Create',
    validationSchema: createWaiverSchema,
  };

  const onOpenNewWaiverForm = () => {
    if (!isSubscriptionActive) {
      openSubscriptionModal();
      return;
    }
    if (requireUpsell) {
      upsell();
      return;
    }
    setNewWaiverForm(waiverForm);
  };

  const rows = React.useMemo(() => {
    const draftWaivers =
      data?.filter(waiver => waiver.status === 'DRAFT') ?? [];
    const tempRows: Row[] =
      draftWaivers.map(draftWaiver => {
        const publishedOption = data?.find(
          waiver =>
            waiver.name === draftWaiver.name && waiver.status === 'PUBLISHED',
        );
        const status = publishedOption ? 'Published' : 'Draft';
        return {
          rowId: draftWaiver.id,
          cells: [
            draftWaiver.name,
            status,
            publishedOption?.publishedAt
              ? format(new Date(publishedOption.publishedAt), 'MMMM d, yyyy')
              : 'Not published',
            <TableDropdownMenu
              options={
                [
                  {
                    label: 'Edit',
                    action: () => editWaiver(draftWaiver.id),
                  },
                  {
                    label: 'View Submissions',
                    action: () => viewWaiverSubmissions(draftWaiver.id),
                  },
                  {
                    label: 'Preview',
                    action: () =>
                      previewWaiver({
                        draftId: draftWaiver.id,
                        publishedId: publishedOption?.id,
                      }),
                  },
                  {
                    label: 'Rename',
                    action: () =>
                      renameWaiver({
                        id: draftWaiver.id,
                        name: draftWaiver.name,
                      }),
                  },
                  !draftWaiver.isDefault && {
                    label: 'Archive',
                    action: () =>
                      archiveWaiver(
                        draftWaiver.id,
                        draftWaiver.services.map(s => s.serviceId),
                      ),
                  },
                ].filter(Boolean) as React.ComponentProps<
                  typeof TableDropdownMenu
                >['options']
              }
              disabled={false}
            />,
          ],
        };
      }) || [];
    return tempRows;
  }, [
    data,
    editWaiver,
    viewWaiverSubmissions,
    previewWaiver,
    renameWaiver,
    archiveWaiver,
  ]);

  const onRowClick = (rowId: string) => {
    onSelectWaiver(rowId);
  };

  const onOpenDefaultWaiver = () => {
    if (!isSubscriptionActive) {
      openSubscriptionModal();
      return;
    }

    if (requireUpsell) {
      upsell();
      return;
    }

    if (!defaultWaiver) return;
    updateMutation.mutate({
      waiverId: defaultWaiver.id,
      update: true,
    });
    onSelectWaiver(defaultWaiver.id);
  };

  return (
    <>
      {isLoading ? (
        <PageLoading />
      ) : hasNoUpdatedWaivers ? (
        <OutlinedPageBox>
          <CenteredContentLayout
            image={{
              src: '/waivers.svg',
              alt: 'Add an item',
            }}
            title="Create a liability waiver"
            description="Create your first liability waiver so that appointments through Porter automatically have waivers tied to them for clients to complete"
            button={{
              text: 'Create Your Waiver',
              onClick: onOpenDefaultWaiver,
            }}
            maxWidth="750px"
            imageMaxWidth="120px"
          />
        </OutlinedPageBox>
      ) : (
        <>
          <Box className="header-right">
            <Button
              variant="contained"
              className="primary"
              onClick={onOpenNewWaiverForm}>
              Create New Waiver
            </Button>
          </Box>
          <ReuseableTable
            name="Your Waivers"
            emptyMessage="No waivers have been created yet"
            headerCells={[
              { id: 'name', label: 'Waiver Name' },
              { id: 'status', label: 'Status' },
              { id: 'publishedAt', label: 'Last Published' },
              { id: 'action', label: '' },
            ]}
            loading={false}
            rows={rows}
            handleRowClick={onRowClick}
          />
        </>
      )}
      <FormSmallModal
        form={newWaiverForm}
        open={!!newWaiverForm}
        onClose={() => setNewWaiverForm(null)}
        alert={{
          title: 'New waiver',
          subtitle:
            'Create a new waiver if you need to have clients sign different waivers for different services.',
          dismissible: false,
          type: 'info',
        }}
      />
    </>
  );
}

export default WaiversTable;
